import {FC, useId, useMemo} from 'react';
import styled from 'styled-components';
import {useParams} from 'wouter';

import {Flex} from '@shared_web/components/core2/flex';

import {Title} from '@mavismart_frontend/components/title';
import {translate} from '@mavismart_frontend/components/translate';
import {GenericCell} from '@mavismart_frontend/pages/cells/generic_cell';
import {DesktopTemplate} from '@mavismart_frontend/pages/desktop_template';
import {NotificationPage} from '@mavismart_frontend/pages/notification_page';
import {NotificationsList} from '@mavismart_frontend/pages/tabs/notifications_list';
import {NotificationsParams} from '@mavismart_frontend/pages/tabs/notifications_params';
import {UserAccount} from '@mavismart_frontend/pages/user_account';
import {getUserLanguage, getUserLocale} from '@mavismart_frontend/stores/account_store';
import {useAccount} from '@mavismart_frontend/stores/account_store';

// Unified desktop page that keeps side lists mounted across route changes to preserve scroll
export const DesktopAccount: FC = () => {
  const {page} = useParams<{page?: string}>();
  const account = useAccount();
  const lang = getUserLanguage(account);
  const locale = getUserLocale(account);
  const accountId = useId();
  const notificationsId = useId();

  const {columns, content} = useMemo(() => {
    const isAccountPage = page === 'account';
    const isNotificationsPage = page === 'notifications';
    const isSettingsPage = page === 'settings';
    const columns = [
      <Menu key="menu" $column $width="100%" $padding={10} $gap={10}>
        <GenericCell
          id={accountId}
          title={translate('user_account', lang).toLocaleUpperCase(locale)}
          to="/user/account"
          rightIcon={<></>}
          highlighted={isAccountPage}
        />
        <GenericCell
          id={notificationsId}
          title={translate('notifications', lang).toLocaleUpperCase(locale)}
          to="/user/notifications"
          rightIcon={<></>}
          highlighted={isNotificationsPage}
        />
        <GenericCell
          id={notificationsId}
          title={translate('settings', lang).toLocaleUpperCase(locale)}
          to="/user/settings"
          rightIcon={<></>}
          highlighted={isSettingsPage}
        />
      </Menu>,
    ];
    if (isAccountPage) {
      return {
        columns,
        content: (
          <Content>
            <UserAccount />
          </Content>
        ),
      };
    }
    if (isNotificationsPage) {
      return {
        columns: [...columns, <NotificationsList key="notifications-list" />],
        content: <NotificationPage />,
      };
    }
    if (isSettingsPage) {
      return {
        columns,
        content: (
          <Content>
            <Title name={translate('notifications_settings', lang)} />
            <NotificationsParams />
          </Content>
        ),
      };
    }
    return {columns: [], content: <></>};
  }, [page, lang, accountId, notificationsId, locale]);

  return <DesktopTemplate columns={columns} columnsWidth={['230px', '370px']} content={content} />;
};

DesktopAccount.displayName = 'DesktopHome';

const Menu = styled(Flex)`
  margin-left: -10px;
  width: calc(100% + 20px);
  a {
    font-size: 16px;
    padding: 16px;
    * {
      font-weight: 500;
    }
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 600px;
  height: 100%;
  margin: 0 auto;
  gap: 16px;
  padding: 16px 0;
`;
